// store/index.js

import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import { API_ROUTES } from '@/axios'
import router, { checkToken } from '@/router' // Fix: import router as default export
Vue.use(Vuex)

const store = new Vuex.Store({
  state: {
    /* User */
    userName: null,
    userEmail: null,
    userAvatar: null,
    currentUser: null,
    userId: null, // Add this line for user ID
    ext: null, // Add extension property
    isSuperuser: false, // If user is superuser
    isMultiClientAdmin: false, // If user has 2+ clients and is not superuser
    assignedClients: [], // Clients assigned to user
    userTheme: 'xperienceiq', // User theme preference
    userLanguage: 'es', // User language preference

    /* NavBar */
    isNavBarVisible: true,

    /* FooterBar */
    isFooterBarVisible: true,

    /* Aside */
    isAsideVisible: true,
    isAsideMobileExpanded: false,

    /* Sample data (commonly used) */
    clients: [],

    /* Selected client */
    selectedClient: null
  },
  getters: {
    /* Get current client info */
    getCurrentClient: (state) => {
      if (!state.selectedClient || !state.clients.length) return null
      return state.clients.find(client => client.alias === state.selectedClient) || null
    }
  },
  mutations: {
    /* A fit-them-all commit */
    basic (state, payload) {
      state[payload.key] = payload.value
    },

    /* User */
    user (state, payload) {
      if (payload.name) {
        state.userName = payload.name
      }
      if (payload.email) {
        state.userEmail = payload.email
      }
      if (payload.avatar) {
        state.userAvatar = payload.avatar
      }
      if (payload.currentUser) {
        state.currentUser = payload.currentUser
        localStorage.setItem('currentUser', payload.currentUser)
      }
      if (payload.userId) {
        state.userId = payload.userId
        localStorage.setItem('userId', payload.userId)
      }
      if (payload.ext) {
        state.ext = payload.ext
        localStorage.setItem('ext', payload.ext)
      }
      if (payload.isSuperuser !== undefined) {
        state.isSuperuser = payload.isSuperuser
      }
      if (payload.isMultiClientAdmin !== undefined) {
        state.isMultiClientAdmin = payload.isMultiClientAdmin
      }
      if (payload.assignedClients) {
        state.assignedClients = payload.assignedClients
      }
    },

    /* Aside Mobile */
    asideMobileStateToggle (state, payload = null) {
      const htmlClassName = 'has-aside-mobile-expanded'

      let isShow

      if (payload !== null) {
        isShow = payload
      } else {
        isShow = !state.isAsideMobileExpanded
      }

      if (isShow) {
        document.documentElement.classList.add(htmlClassName)
      } else {
        document.documentElement.classList.remove(htmlClassName)
      }

      state.isAsideMobileExpanded = isShow
    },

    /* Full Page mode */
    fullPage (state, payload) {
      state.isNavBarVisible = !payload
      state.isAsideVisible = !payload
      state.isFooterBarVisible = !payload
    },

    /* Selected Client */
    setSelectedClient (state, clientAlias) {
      state.selectedClient = clientAlias
      if (clientAlias) {
        localStorage.setItem('selectedClient', clientAlias)
      } else {
        localStorage.removeItem('selectedClient')
      }
    },

    /* Update clients list */
    setClients (state, clients) {
      state.clients = clients
    },

    /* User theme */
    setUserTheme (state, theme) {
      state.userTheme = theme
      // Aplicar el tema al DOM
      document.documentElement.setAttribute('data-theme', theme)
      // Guardar en localStorage para persistencia
      localStorage.setItem('userTheme', theme)
    },

    /* User language */
    setUserLanguage (state, language) {
      state.userLanguage = language
      // Cambiar el idioma de i18n si está disponible
      if (window.i18n) {
        window.i18n.locale = language
      }
      // Guardar en localStorage para persistencia
      localStorage.setItem('userLanguage', language)
    }
  },
  actions: {
    async fetchCurrentUser ({ commit }) {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          throw new Error('No token found')
        }

        const response = await axios.get(API_ROUTES.ME, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })

        const user = response.data
        const isSuperuser = user.is_superuser || false
        const isMultiClientAdmin = user.is_multi_client_admin || false
        const assignedClients = user.assigned_clients || user.clientes || []

        commit('user', {
          name: user.nom + ' ' + user.cognoms,
          email: user.email,
          avatar: user.avatar,
          currentUser: user.nom_usuari,
          userId: user.id,
          isSuperuser,
          isMultiClientAdmin,
          assignedClients
        })

        // Guardar clientes asignados también en la lista de clientes
        if (assignedClients.length > 0) {
          commit('setClients', assignedClients)
        }

        // Auto-seleccionar cliente si el usuario tiene exactamente 1 cliente
        if (!isSuperuser && assignedClients.length === 1) {
          commit('setSelectedClient', assignedClients[0].alias)
        } else if (!isSuperuser && assignedClients.length > 1 && !localStorage.getItem('selectedClient')) {
          // Si tiene múltiples clientes y no hay uno seleccionado, seleccionar el primero
          commit('setSelectedClient', assignedClients[0].alias)
        }
      } catch (error) {
        const isValidToken = await checkToken()
        if (!isValidToken) {
          localStorage.removeItem('token')
          router.push('/login')
        } else {
          console.error('Error fetching current user:', error)
        }
      }
    },

    asideDesktopOnlyToggle (store, payload = null) {
      let method

      switch (payload) {
        case true:
          method = 'add'
          break
        case false:
          method = 'remove'
          break
        default:
          method = 'toggle'
      }
      document.documentElement.classList[method]('has-aside-desktop-only-visible')
    },
    toggleFullPage ({ commit }, payload) {
      commit('fullPage', payload)

      document.documentElement.classList[!payload ? 'add' : 'remove']('has-aside-left', 'has-navbar-fixed-top')
    },
    fetch ({ commit }, payload) {
      axios
        .get(`data-sources/${payload}.json`)
        .then((r) => {
          if (r.data && r.data.data) {
            commit('basic', {
              key: payload,
              value: r.data.data
            })
          }
        })
        .catch(error => {
          alert(error.message)
        })
    },

    /* Initialize store from localStorage */
    initializeStore ({ commit }) {
      // Cargar cliente desde localStorage únicamente
      const savedClient = localStorage.getItem('selectedClient')

      if (savedClient) {
        commit('setSelectedClient', savedClient)
      }

      // Cargar idioma desde localStorage si existe
      const savedLanguage = localStorage.getItem('userLanguage')
      if (savedLanguage) {
        commit('setUserLanguage', savedLanguage)
      }
    },

    /* Load user theme */
    async loadUserTheme ({ commit }) {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          // Si no hay token, aplicar tema por defecto
          commit('setUserTheme', 'xperienceiq')
          return
        }

        const response = await axios.get(API_ROUTES.GET_USER_CONFIG, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })

        const theme = response.data.theme || 'xperienceiq'
        commit('setUserTheme', theme)
      } catch (error) {
        console.error('Error loading user theme:', error)
        // Si falla, intentar cargar de localStorage o usar por defecto
        const savedTheme = localStorage.getItem('userTheme') || 'xperienceiq'
        commit('setUserTheme', savedTheme)
      }
    },

    /* Update user theme */
    async updateUserTheme ({ commit }, theme) {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          throw new Error('No token found')
        }

        await axios.put(API_ROUTES.UPDATE_USER_THEME, {
          theme
        }, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })

        commit('setUserTheme', theme)
        return { success: true }
      } catch (error) {
        console.error('Error updating user theme:', error)
        return { success: false, error }
      }
    },

    /* Load user language */
    async loadUserLanguage ({ commit }) {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          // Si no hay token, aplicar idioma por defecto
          commit('setUserLanguage', 'es')
          return
        }

        const response = await axios.get(API_ROUTES.GET_USER_CONFIG, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })

        const language = response.data.language || 'es'
        commit('setUserLanguage', language)
      } catch (error) {
        console.error('Error loading user language:', error)
        // Si falla, intentar cargar de localStorage o usar por defecto
        const savedLanguage = localStorage.getItem('userLanguage') || 'es'
        commit('setUserLanguage', savedLanguage)
      }
    },

    /* Update user language */
    async updateUserLanguage ({ commit }, language) {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          throw new Error('No token found')
        }

        await axios.put(API_ROUTES.UPDATE_USER_LANGUAGE, {
          language
        }, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })

        commit('setUserLanguage', language)
        return { success: true }
      } catch (error) {
        console.error('Error updating user language:', error)
        return { success: false, error }
      }
    }
  }
})

export default store
